import { useState } from 'react';
import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';
import { useGetBuyList } from '~/api/buyList';
import type { RootState } from '~/redux/store';
import { type ClientError, isNotFoundGraphQLError } from '~/utils';
import { RetryBanner } from '~/components/ui/RetryBanner';
import {
  FOCUSED_REQUEST_FOR_QUOTE_PARAM,
  isFirstHistoryEntry,
  isReachedThroughRequestForQuoteRedirect,
} from '~/utils/requestForQuoteLinks';
import { Button } from '~/components/ui';
import { BuyListSummaryTable } from './sections/BuyListSummaryTable';
import { BuyListItemsSection } from './sections/BuyListItemsSection';
import { BuyListQuoteComparison } from './sections/quote-comparison/BuyListQuoteComparison';
import { useBuyListQuoteComparisons } from './sections/quote-comparison/useBuyListQuoteComparisons';
import { RequestForQuoteModal } from './sections/request-for-quote/RequestForQuoteModal';
import { AddBuyListToCartModal } from './sections/add-to-cart/AddBuyListToCartModal';
import { BuyListStatusBadge } from './sections/BuyListStatusBadge';
import { BuyListStatusActions } from './sections/BuyListStatusActions';

export default function BuyListDetail() {
  const { buyListId } = useParams<{ buyListId: string }>();

  if (!buyListId) {
    return <Navigate to="/buy-lists" replace />;
  }

  return <BuyListDetailContent buyListId={buyListId} />;
}

function BuyListDetailContent({ buyListId }: { readonly buyListId: string }) {
  const { t } = useTranslation('inventory');
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const focusedRequestForQuoteId =
    searchParams.get(FOCUSED_REQUEST_FOR_QUOTE_PARAM) || null;
  const currentUserId = useSelector(
    (state: RootState) => state.auth.user?.id ?? null
  );

  const [isRequestQuoteModalOpen, setIsRequestQuoteModalOpen] = useState(false);
  const [isAddToCartModalOpen, setIsAddToCartModalOpen] = useState(false);

  const { data, isLoading, isError, error, refetch } = useGetBuyList(buyListId);
  const buyList = data?.buyList ?? null;
  const quoteComparisons = useBuyListQuoteComparisons(buyListId, {
    enabled: !!buyList,
  });
  const isMissingBuyList = isError
    ? isNotFoundGraphQLError(error as ClientError)
    : !buyList;
  const isUnreadableBuyList = isError && !isMissingBuyList;

  const goBackToBuyLists = () => {
    if (
      isFirstHistoryEntry(location) ||
      isReachedThroughRequestForQuoteRedirect(location.state)
    ) {
      void navigate('/buy-lists');
      return;
    }
    void navigate(-1);
  };

  const initiatingUser = buyList?.initiatingUser ?? null;
  const isViewersOwnList = Boolean(
    initiatingUser && currentUserId && initiatingUser.id === currentUserId
  );
  const canActOnLines =
    buyList !== null &&
    buyList.status === 'OPEN' &&
    (buyList.items.length > 0 || buyList.tallies.length > 0);

  return (
    <div className="w-full flex flex-col gap-6" data-testid="buy-list-detail">
      <title>{t('inventory.buy_list.page_title')}</title>

      <section
        className="flex flex-col gap-2"
        data-testid="buy-list-detail-title-section"
      >
        <button
          type="button"
          onClick={goBackToBuyLists}
          data-testid="buy-list-detail-back"
          className="inline-flex items-center gap-1.5 text-sm text-neut-sec hover:text-neut-prim transition-colors w-fit"
        >
          <ArrowLeft size={14} />
          {t('inventory.buy_list.back')}
        </button>

        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-semibold text-neut-prim">
              {t('inventory.buy_list.detail_heading')}
            </h1>
            {buyList && (
              <BuyListStatusBadge
                status={buyList.status}
                testId="buy-list-detail-status"
              />
            )}
          </div>
          <div className="flex items-center gap-3">
            {buyList && isViewersOwnList && (
              <BuyListStatusActions
                buyListId={buyListId}
                status={buyList.status}
                openRequestsForQuote={quoteComparisons.openRequestsForQuote}
                areAllRequestsForQuoteLoaded={
                  quoteComparisons.areAllRequestsForQuoteLoaded
                }
              />
            )}
            <Button
              variant="secondary"
              width="auto"
              data-testid="buy-list-add-to-cart-button"
              disabled={!canActOnLines}
              onClick={() => setIsAddToCartModalOpen(true)}
            >
              {t('inventory.buy_list.add_to_cart_button')}
            </Button>
            <Button
              variant="primary"
              width="auto"
              data-testid="buy-list-request-quote-button"
              disabled={!canActOnLines}
              onClick={() => setIsRequestQuoteModalOpen(true)}
            >
              {t('inventory.buy_list.request_quote_button')}
            </Button>
          </div>
        </div>
      </section>

      {isLoading && (
        <p
          className="text-sm text-neut-sec"
          data-testid="buy-list-detail-loading"
        >
          {t('inventory.buy_list.detail_loading')}
        </p>
      )}

      {!isLoading && isUnreadableBuyList && (
        <RetryBanner
          message={t('inventory.buy_list.detail_load_failed')}
          retryLabel={t('inventory.retry')}
          onRetry={() => void refetch()}
          testId="buy-list-detail"
        />
      )}

      {!isLoading && isMissingBuyList && (
        <p
          className="text-sm text-neut-sec"
          data-testid="buy-list-detail-not-found"
        >
          {t('inventory.buy_list.detail_not_found')}
        </p>
      )}

      {buyList && (
        <>
          <BuyListItemsSection
            buyListId={buyListId}
            transportVehicleId={buyList.transportVehicle.id}
            items={buyList.items}
            tallies={buyList.tallies}
            canManageItems={isViewersOwnList && buyList.status === 'OPEN'}
            platformProductLengthIdsInSellerQuotes={
              quoteComparisons.platformProductLengthIdsInSellerQuotes
            }
            summary={
              <BuyListSummaryTable
                buyList={buyList}
                testId="buy-list-detail-summary-table"
                showBuyerColumn={!isViewersOwnList}
              />
            }
            viewerId={currentUserId}
          />

          <BuyListQuoteComparison
            comparisons={quoteComparisons}
            focusedRequestForQuoteId={focusedRequestForQuoteId}
          />

          {isRequestQuoteModalOpen && (
            <RequestForQuoteModal
              onClose={() => setIsRequestQuoteModalOpen(false)}
              buyListId={buyListId}
              items={buyList.items}
              tallies={buyList.tallies}
              expectedShipDate={buyList.expectedShipDate}
            />
          )}

          {isAddToCartModalOpen && (
            <AddBuyListToCartModal
              onClose={() => setIsAddToCartModalOpen(false)}
              buyListId={buyListId}
              items={buyList.items}
              tallies={buyList.tallies}
              initialLocationId={buyList.location.id}
              initialTransportVehicleId={buyList.transportVehicle.id}
            />
          )}
        </>
      )}
    </div>
  );
}
